/* eslint-disable-next-line */
import { twMerge } from 'tailwind-merge';
import Icon from '../icon/icon';

export interface BadgeProps {
    size?: 'xs' | 'sm';
    variant?: 'default' | 'contrast' | 'outlined';
    className?: string;
    style?: React.CSSProperties;
    type?: 'primary' | 'secondary' | 'gray' | 'error' | 'success' | 'variant';
    iconName?: string;
    label: string;
    position?: 'left' | 'right';
    testId?: string;
}

export function Badge(props: BadgeProps) {
    const background = {
        primary: {
            default: 'bg-primary hover:bg-primary-700 disabled:bg-primary-100 text-white',
            contrast:
                'bg-primary-50 hover:bg-primary-400 disabled:bg-primary-50 text-primary hover:text-white disabled:text-white',
            outlined:
                'bg-white border border-primary hover:bg-primary-50 disabled:border-primary-100 disabled:bg-white text-primary',
        },
        secondary: {
            default: 'bg-secondary hover:bg-secondary-700 disabled:bg-secondary-100 text-white',
            contrast:
                'bg-secondary-50 hover:bg-secondary-400 disabled:bg-secondary-50 text-secondary hover:text-white disabled:text-white',
            outlined:
                'bg-white border border-secondary hover:bg-secondary-50 disabled:border-secondary-100 disabled:bg-white text-secondary',
        },
        gray: {
            default: 'bg-gray-900 hover:bg-gray-700 disabled:bg-gray-100 text-white',
            contrast:
                'bg-gray-50 hover:bg-gray-400 disabled:bg-gray-50 text-gray-900 hover:text-white disabled:text-white',
            outlined:
                'bg-white border border-gray-900 hover:bg-gray-50 disabled:border-gray-100 disabled:bg-white text-gray-900',
        },
        error: {
            default: 'bg-error-200 hover:bg-error-100 disabled:bg-error-50 text-white',
            contrast:
                'bg-error-50  hover:bg-error-200 disabled:bg-error-100 text-error-200 hover:text-white disabled:text-white',
            outlined:
                'bg-white border border-error-100 hover:bg-error-50 disabled:bg-white disabled:border-error-50 text-error-200',
        },
        success: {
            default: 'bg-success-200 hover:bg-success-100 disabled:bg-success-50 text-white',
            contrast:
                'bg-success-50  hover:bg-success-200 disabled:bg-success-100 text-success-200 hover:text-white disabled:text-white',
            outlined:
                'bg-white border border-success-100 hover:bg-success-50 disabled:bg-white disabled:border-success-50 text-success-200',
        },
        variant: {
            default: 'bg-variant-800 hover:bg-variant-500 disabled:bg-variant-100 text-white',
            contrast:
                'bg-variant-50  hover:bg-variant-200 disabled:bg-variant-100 text-variant-800 hover:text-white disabled:text-white',
            outlined:
                'bg-white border border-variant-800 hover:bg-variant-50 disabled:bg-white disabled:border-variant-50 text-variant-800',
        },
    };

    const icons = {
        primary: {
            default: 'text-white',
            contrast: 'text-primary group-hover:text-white group-disabled:text-white',
            outlined: 'text-primary',
        },
        secondary: {
            default: 'text-white',
            contrast: 'text-secondary group-hover:text-white group-disabled:text-white',
            outlined: 'text-secondary',
        },
        gray: {
            default: 'text-white',
            contrast: 'text-gray-900 group-hover:text-white group-disabled:text-white',
            outlined: 'text-gray-900',
        },
        error: {
            default: 'text-white',
            contrast: 'text-error-200 group-hover:text-white group-disabled:text-white',
            outlined: 'text-error-200',
        },
        success: {
            default: 'text-white',
            contrast: 'text-success-200 group-hover:text-white group-disabled:text-white',
            outlined: 'text-success-200',
        },
        variant: {
            default: 'text-white',
            contrast: 'text-variant-800 group-hover:text-white group-disabled:text-white',
            outlined: 'text-variant-800',
        },
    };

    const classes = twMerge(
        'inline-flex px-2 text-xs group ',
        props.position === 'right' && 'flex-row-reverse',
        props.size === 'xs' ? 'py-1 rounded-full' : 'py-2 rounded',
        background[props.type || 'primary'][props.variant || 'default'],
        props.className,
    );

    const innerClasses = twMerge(icons[props.type || 'primary'][props.variant || 'default']);
    return (
        <div className={classes} data-testid={props.testId} style={props.style}>
            {props.iconName && <Icon className={innerClasses} name={props.iconName} size={14} />}
            <p className={props.position === 'right' ? 'mr-1' : 'ml-1'}>{props.label}</p>
        </div>
    );
}

Badge.defaultProps = {
    size: 'xs',
    variant: 'default',
    type: 'primary',
    position: 'left',
};

export default Badge;
